import MenuIcon from "@mui/icons-material/Menu";
import { Box, Container, Divider, IconButton, Stack, Toolbar, Typography } from "@mui/material";
import type { UserInfo } from "firebase/auth";
import type { MouseEvent } from "react";
import { useState } from "react";
import logoImg from "../../../images/logo-przezmorze.svg";
import { useAgentWithId } from "../admin/agents/useAgentWithId";
import type { CustomerContext } from "../auth/auth";
import { useCustomerWithId } from "../customers/useCustomerWithId";
import { AccountManagerAvatar } from "./AccountManagerAvatar";
import { AppBarAvatar } from "./AppBarAvatar";
import Office from "./Office";
import { TopmostBarMenu } from "./TopmostBarMenu";
import { TopmostMobileMenu } from "./TopmostMobileMenu";

interface Props {
  readonly context: CustomerContext | null;
  readonly userInfo: UserInfo | null;
}

export function TopmostAppBar({ context, userInfo }: Props) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const openMenu = Boolean(menuAnchor);
  const handleMenuClick = (event: MouseEvent) => setMenuAnchor(event.currentTarget as HTMLElement);
  const handleMenuClose = () => setMenuAnchor(null);

  const { customer } = useCustomerWithId(context?.customerId);
  const { data: agent } = useAgentWithId(customer?.accountManager?.id);

  return (
    <Box sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)", backgroundColor: "white", color: "black" }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ justifyContent: "space-between", minHeight: "64px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Przycisk Menu na Mobile */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 2, display: { md: "none" }, ml: -1 }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src={logoImg}
              alt="Przezmorze.pl"
              sx={{ height: 34, width: "auto", cursor: "pointer" }}
              onClick={() => (window.location.hash = "")}
            />
          </Box>

          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Divider orientation="vertical" flexItem sx={{ mx: 2, display: { xs: "none", md: "block" } }} />

            <AccountManagerAvatar value={customer?.accountManager} />
            <Office />
            <Typography
              variant="h6"
              component="a"
              href={agent?.contact.phone ? `tel:${agent.contact.phone.replace(/\s+/g, "")}` : undefined}
              sx={{
                fontWeight: "bold",
                textDecoration: "none",
                color: "text.primary",
                "&:hover": { color: "primary.main" },
              }}
            >
              +48 781 360 360
            </Typography>
            <AppBarAvatar onClick={handleMenuClick} openMenu={openMenu} userInfo={userInfo} />
          </Stack>
        </Toolbar>
      </Container>
      <TopmostBarMenu open={openMenu} onClose={handleMenuClose} anchorEl={menuAnchor} agent={agent} context={context} />
      <TopmostMobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} context={context} />
    </Box>
  );
}
